import { useMemo } from 'react';
import { Box, Link, Text } from '@nimbus-ds/components';
import md5 from 'crypto-js/md5';
import {
  OrderChargebackResponseDto,
  OrderChargebackWithOrderDataType,
} from '@tiendanube/common';
import { AlertSessionStorage } from 'App/components';
import { PAGO_NUBE_PROVIDER_NAME } from 'commons/constants/paymentProviders';
import useOrdersChargeback from 'domains/Orders/Orders/hooks/useOrdersChargeback';
import {
  trackingOrdersChargebackOrderClick,
  trackingOrdersChargebackNuvemPagoClick,
  trackingOrdersChargebackExitButtonClick,
  trackingOrdersChargebackDeadlineOrderClick,
  trackingOrdersChargebackDeadlineNuvemPagoClick,
  trackingOrdersChargebackDeadlineExitButtonClick,
} from 'domains/Orders/tracking';
import useTranslationOrders from 'domains/Orders/useTranslationOrders/useTranslationOrders';
import { useNuvemPagoRedirect } from '../../../hooks';

const ORDER_CHARGEBACK_EMPTY_RESPONSE: OrderChargebackResponseDto = {
  pending: [],
  disputeRejected: [],
  resolvedCustomer: [],
  resolvedMerchant: [],
  resolvedWithoutDispute: [],
};

function ChargebackAlert(): JSX.Element {
  const t = useTranslationOrders();
  const { chargebacks, isChargebackAvailable } = useOrdersChargeback();
  const { transactionsURL, listTransactionsURL } = useNuvemPagoRedirect(
    PAGO_NUBE_PROVIDER_NAME,
  );

  const { pendingNearDeadlineChargebacks, pendingLongDeadlineChargebacks } =
    useMemo(() => {
      if (!isChargebackAvailable || !chargebacks) {
        return {
          pendingNearDeadlineChargebacks: ORDER_CHARGEBACK_EMPTY_RESPONSE,
          pendingLongDeadlineChargebacks: ORDER_CHARGEBACK_EMPTY_RESPONSE,
        };
      }

      const chargebacksNearDeadline: OrderChargebackResponseDto = {
        pending: [],
        disputeRejected: [],
        resolvedCustomer: [],
        resolvedMerchant: [],
        resolvedWithoutDispute: [],
      };
      const chargebacksLongDeadline: OrderChargebackResponseDto = {
        pending: [],
        disputeRejected: [],
        resolvedCustomer: [],
        resolvedMerchant: [],
        resolvedWithoutDispute: [],
      };

      const now = new Date();
      for (const cb of chargebacks.pending) {
        // Skip if required fields are missing
        if (
          cb.orderId === null ||
          cb.orderNumber === null ||
          cb.deadline === null
        ) {
          continue;
        }

        const deadline = new Date(cb.deadline);
        const diffHours = (deadline.getTime() - now.getTime()) / 36e5;

        // If the deadline is in the past, skip
        if (diffHours < 0) {
          continue;
        }

        // If the deadline is within the next 24 hours, it's near deadline
        if (diffHours <= 24) {
          chargebacksNearDeadline.pending.push(cb);
        } else {
          chargebacksLongDeadline.pending.push(cb);
        }
      }

      return {
        pendingNearDeadlineChargebacks: chargebacksNearDeadline,
        pendingLongDeadlineChargebacks: chargebacksLongDeadline,
      };
    }, [isChargebackAvailable, chargebacks]);

  const dueDateToShow = useMemo(() => {
    if (pendingLongDeadlineChargebacks.pending.length === 0) {
      return undefined;
    }

    return pendingLongDeadlineChargebacks.pending
      .map((cb) => new Date(cb.deadline))
      .sort((a, b) => a.getTime() - b.getTime())[0];
  }, [pendingLongDeadlineChargebacks]);

  const orderPendingLinks = (
    pendingChargebacks: OrderChargebackWithOrderDataType[],
    type?: 'longDeadline' | 'nearDeadline',
  ) =>
    [...pendingChargebacks]
      .filter((cb) => cb.orderId && cb.orderNumber)
      .sort((a, b) => a.orderId - b.orderId)
      .map((cb, idx, arr) => (
        <span key={cb.orderNumber} style={{ marginRight: 4 }}>
          <Link
            onClick={() => {
              if (type === 'longDeadline') {
                trackingOrdersChargebackDeadlineOrderClick();
              } else {
                trackingOrdersChargebackOrderClick();
              }
            }}
            as="a"
            href={transactionsURL(cb.orderId.toString())}
          >
            #{cb.orderNumber}
          </Link>
          {idx < arr.length - 1 && ','}
        </span>
      ));

  const dueDateString = (dueDate: Date) => {
    const date = new Date(dueDate as Date);
    const day = date.toLocaleDateString('es-AR', { day: '2-digit' });
    const month = date.toLocaleDateString('es-AR', { month: 'long' });
    return `${day} ${month}`;
  };

  const getHashToCompare = (
    pendingChargebacks: OrderChargebackWithOrderDataType[],
  ): string => {
    const hash = md5(
      pendingChargebacks
        .filter((cb) => cb.orderId !== null)
        .map((cb) => cb.orderId)
        .sort((a, b) => a - b)
        .join(','),
    ).toString();
    return hash;
  };

  return (
    <>
      {isChargebackAvailable &&
        pendingLongDeadlineChargebacks.pending.length > 0 && (
          <AlertSessionStorage
            keyName={
              'chargeback-alert-' +
              getHashToCompare(pendingLongDeadlineChargebacks.pending)
            }
            title={t('chargeback.alertPending.title', {
              count: pendingLongDeadlineChargebacks.pending.length,
            })}
            appearance="warning"
            onRemove={() => {
              trackingOrdersChargebackExitButtonClick();
            }}
          >
            <Box display="flex" flexWrap="wrap" alignItems="center">
              <Text>
                {t('chargeback.alertPending.description_due_date', {
                  due_date: dueDateToShow ? dueDateString(dueDateToShow) : '',
                })}
                {orderPendingLinks(
                  pendingLongDeadlineChargebacks.pending,
                  'longDeadline',
                )}
              </Text>
            </Box>
            <Link
              as="a"
              href={listTransactionsURL()}
              onClick={() => {
                trackingOrdersChargebackNuvemPagoClick();
              }}
            >
              {t('chargeback.alertPending.link')}
            </Link>
          </AlertSessionStorage>
        )}
      {isChargebackAvailable &&
        pendingNearDeadlineChargebacks.pending.length > 0 && (
          <AlertSessionStorage
            keyName={
              'chargeback-alert-' +
              getHashToCompare(pendingNearDeadlineChargebacks.pending)
            }
            title={t('chargeback.alertPending.title_deadline_near')}
            appearance="warning"
            onRemove={() => {
              trackingOrdersChargebackDeadlineExitButtonClick();
            }}
          >
            <Box display="flex" flexWrap="wrap" alignItems="center">
              <Text>
                {t('chargeback.alertPending.description_reference', {
                  count: pendingNearDeadlineChargebacks.pending.length,
                })}
                {orderPendingLinks(pendingNearDeadlineChargebacks.pending)}
              </Text>
            </Box>
            <Link
              as="a"
              href={listTransactionsURL()}
              onClick={() => {
                trackingOrdersChargebackDeadlineNuvemPagoClick();
              }}
            >
              {t('chargeback.alertPending.link')}
            </Link>
          </AlertSessionStorage>
        )}
    </>
  );
}

export default ChargebackAlert;
